import { InteropCodeId } from '@/constants/dmp-connect'

type BeneficiaryVitaleData = {
  s_name: string
  s_birthName: string
  s_givenName: string
  s_birthday: string
  i_rank: number
  i_qualBenef: number
  NirData: {
    Nir: any
    CertifiedNir: any
    s_certificationDate: string
  }
  HealthCoverage: {
    s_regime: string
    s_desk: string
    s_center: string
  }
}

export type CardReader = {
  i_accessMode: number
  i_slotType: CardReaderSlotType
  s_accessMode: string
  s_name: string
  s_slotType: string
}

export enum CardReaderSlotType {
  NO_CARD = 1, // Aucune carte insérée
  VITALE_CARD = 2, // Une carte vitale insérée
  CPX_CARD = 3, // Une carte CPS insérée
  UNKNOWN_CARD = 4, // Une carte inconnue insérée
  UNDETECTED = 5, // Détection impossible
}

export enum CPxCardStatusEnum {
  VALID_CARD_INSERTED = 1, // Une carte valide est insérée
  NO_CARD_INSERTED = 2, // Aucune carte insérée
  UNKNOWN_CARD_INSERTED = 3, // Carte inconnue insérée
  CARD_READER_STATUS_CHANGED = 4, // L’état du lecteur a changé depuis le dernier test (carte insérée, extraite)
  CARD_BLOCKED = 5, // Carte bloquée (Trop grand nombre d’essai erroné de connexion avec un code PIN incorrect)
  CARD_EXPIRED = 6, // La validité de la carte insérée est expirée
  OTHER = 7, // Autre erreur (erreur de configuration, …)
}

export enum CPxType {
  CPS = 1, // Carte de Professionnel de Santé
  CPF = 2, // Carte de Professionnel de santé en Formation
  CPE = 3, // Carte de Personnel d'Etablissement (CDE, CPE)
  CPA = 4, // Carte de Personnel Administratif (CPA, CDA)
}

export type InteropCode = {
  s_code: string
  s_codeSystem: string
  s_displayName: string
}

type Ins = {
  s_value: string
  s_key: string
  s_oid: string
  s_startDate: string
  s_endDate: string
}

export type InsIdentity = {
  Ins: Ins
  s_lastInsiCallDate: string
  s_birthName: string
  s_given: string
  s_birthGiven: string
  i_sex: number
  s_birthDate: string
  s_birthPlace: string
  History?: InsHistory[]
}

type InsHistory = {
  s_value: string
  s_key: string
  s_oid: string
  s_startDate: string
  s_endDate: string
}

export type LpsInfos = {
  s_idam: string
  s_numAM: string
  s_version: string
  s_instance?: string
  s_name: string
  s_billingNumber?: string
}

export type VitaleCardPatient = {
  HealthCoverage: {
    s_center: string
    s_desk: string
    s_regime: string
  }
  HealthCoverageService: {
    s_code: string
    s_endDate: string
    s_startDate: string
  }
  RightPeriods: [
    {
      s_endDate: string
      s_startDate: string
    }
  ]
  TreatingPhysicianDeclaration: string
  i_birthRank: number
  i_quality: number
  s_addressLine1: string
  s_addressLine2: string
  s_addressLine3: string
  s_addressLine4: string
  s_addressLine5: string
  s_birthday: string
  s_birthdayYYMMDD: string
  s_birthdayYYYYMMDD: string
  s_birthname: string
  s_certifiedNir: string
  s_given: string
  s_insC: string
  s_name: string
  s_nir: string
  s_rawBirthdayYYMMDD: string
  s_rawBirthdayYYYYMMDD: string
}

export enum VitaleCardNatureEnum {
  PROD = 1, // Carte Vitale réelle
  DEMO = 2, // Carte Vitale de démonstration
  TEST = 3, // Carte Vitale de test.
}

export enum VitaleCardStatusEnum {
  ERROR = 1, // Erreur de lecture (erreur du lecteur, carte impossible à lire, …)
  NEW_CARD_INSERTED = 2, // Nouvelle carte insérée depuis le précédent test
  NO_CARD_INSERTED = 4, // Aucune carte insérée dans le lecteur
  UNKNOWN_CARD_INSERTED = 5, // Une carte inconnue est insérée dans le lecteur
}

export enum VitaleCardVersionEnum {
  TYPE_1_IGEA = 1, // Carte Vitale type 1 IGEA
  TYPE_1_SCOT = 2, // Carte Vitale type 1 SCOT
  TYPE_2 = 3, // Carte Vitale type 2
}

export type PracticeLocation = {
  s_practiceLocationActivity: string
  s_practiceLocationBillingNumber: string
  s_practiceLocationHealthcareSettings: string
  s_practiceLocationName: string
  s_practiceLocationStructureId: string
  i_practiceLocationExerciceMode: number
}

// Request
export type BaseParams = {
  s_commandName: string
  s_sessionId?: string
}

export type OpenSessionParams = {
  i_timeoutInSeconds?: number
  s_proxyIpOrUrl?: string
  s_proxyPort?: string
  s_proxyLogin?: string
  s_proxyPass?: string
  s_ntpServer?: string
  i_ntpPort?: number
  i_disableUpdateCheck?: number
  s_updateCheckChannel?: string
  s_dcparameters64: string
  i_getDcparamInformations?: number
  s_tseCredentials?: string
  s_applicationId?: string
  s_licensingGroupId?: string
}

export type GetPcscReadersParams = {
  s_sessionId: string
}

type GetCardByIndex = { i_readerNumber: number }
type GetCardByName = { s_readerName: string }

export type GetCardParams = GetCardByIndex | GetCardByName

export type ReadCPxCardParams = {
  i_returnCertificates?: number
  s_pinCode: string
}

export type ReadVitaleCardParams = {
  i_getXmlContent?: number
}

export type StartCardMonitoringParams = {
  i_checkingInterval: number
}

export type CreateTlsiConnectorParams = {
  s_pinCode: string
  s_tlsiUrl?: string
  i_transactionsTimeout?: number
  s_practiceSetting: string
  i_cpsPracticeLocationIndice: number
}

export type GetInsFromVitaleCardParams = {
  LpsInfos: LpsInfos
  i_vitalePatientIndex: number
}

export type GetInsFromRawVitaleDataParams = {
  LpsInfos: LpsInfos
  BeneficiaryVitaleData: BeneficiaryVitaleData
  RightHolderVitaleData?: any
  s_serialNumber: string
}

export type GetInsFromIdentityInformationParams = {
  LpsInfos: LpsInfos
  s_birthName: string
  s_given: string
  i_sex?: number
  s_birthDate: string
  s_birthPlace?: string
}

export type CheckInsIdentityParams = {
  LpsInfos: LpsInfos
  s_ins: string
  s_key: string
  s_oid: string
  s_birthName: string
  s_given: string
  s_birthDate: string
  i_sex: number
  s_birthPlace?: string
  i_getTransactionFrames?: number
}

export type SubmitInsiBatchValidationSetParams = {
  LpsInfos: LpsInfos
  Patients: Array<{
    s_ins: string
    s_key: string
    s_oid: string
    s_birthName: string
    s_given: string
    s_birthGiven: string
    s_birthDate: string
    s_birthPlace: string
    i_sex: number
  }>
  i_getTransactionFrames?: number
}

export type GetInsiBatchValidationSetResultParams = {
  LpsInfos: LpsInfos
  s_batchId: string
  i_getTransactionFrames?: number
}

export type GetInteropCodesParams = {
  s_codeIds: InteropCodeId[]
}

// Response
export type BaseResponse = {
  s_status: 'OK' | 'ERROR'
}

export type ErrorResponse = BaseResponse & {
  i_apiErrorCode: number
  i_apiErrorType: number
  s_apiErrorDescription: string
  s_apiErrorTypeDescription: string
  s_apiErrorContext: string
  s_apiErrorExtendedInformations: string
}

export type TlsiErrorResponse = ErrorResponse & {
  s_apiErrorTlsiErrorDetailCode: string
  s_apiErrorTlsiErrorDetailError: string
  s_apiErrorTlsiErrorDetailSeverity: string
  s_apiErrorTlsiErrorSoapCode: string
  s_apiErrorTlsiErrorSoapReason: string
  s_apiErrorTlsiErrorSoapSubcode: string
}

export type PcscReadersResponse = BaseResponse & {
  Readers: CardReader[]
}

export type SessionResponse = BaseResponse & {
  s_sessionId: string
}

export type SessionExistsResponse = BaseResponse & {
  i_exists: number
  s_timeout: string // example: "01:00'00\""
  s_remaining: string // example: "00:59'55\""
}

export type GetSessionStateResponse = BaseResponse & {
  i_authToken: number
  i_caCert: number
  i_connection: number
  i_cpxCard: number
  i_cpxReader: number
  i_dmpConnector: number
  i_documentsFound: number
  i_practiceLocation: number
  i_user: number
  i_vitaleCard: number
}

export type GetCpxHpInfosResponse = BaseResponse & {
  Givens: string[]
  i_cpxType: CPxType
  s_birthName: string
  s_exerciceName: string
  s_professionCode: string
  s_specialty: string
  s_usualGiven: string
  s_usualName: string
  s_idNatPs: string
}

export type ReadCPxCardResponse = BaseResponse & {
  PracticeLocations: PracticeLocation[]
  i_cpxCardType: number
  i_cpxType: number
  i_internalIdType: number
  i_remainingPinCodeInputs: number
  s_authenticationCertificatePEM: string
  s_cpxSerialNumber: string
  s_cpxValidityDate: string
  s_given: string
  s_internalId: string
  s_name: string
  s_profession: string
  s_professionDescription: string
  s_professionOid: string
  s_signatureCertificatePEM: string
  s_speciality: string
  s_specialityDescription: string
  i_isAnonymousCpe: number
}

export type ReadVitaleCardResponse = BaseResponse & {
  i_cardStatus: number
  s_cardStatusText: string
  s_serialNumber: string
  s_xmlContentInBase64: string
  Patients: VitaleCardPatient[]
}

export type CheckInsIdentityResponse = BaseResponse

export type SubmitInsiBatchValidationSetResponse = {
  s_batchId: string
  i_estimationDelay: number
  s_processingDate: string
  s_submissionDate: string
  i_requestSize: number
}

export type GetInsiBatchValidationSetResultResponse = BaseResponse & {
  Result: Array<{
    i_validationResult: number
    s_ins: string
    s_key: string
    s_oid: string
  }>
  i_batchValidationResult: number
  s_batchValidationResult: string
  i_estimationDelay: number
}

export type GetInsResponse = BaseResponse & {
  i_insIdentityResult: number
  Identity?: InsIdentity
}

type StartCardMonitoringResponse = BaseResponse & {
  s_cardStatus: string
}

export type StartCpxCardMonitoringResponse = StartCardMonitoringResponse & {
  i_cardStatus: CPxCardStatusEnum
}

export type StartVitaleCardMonitoringResponse = StartCardMonitoringResponse & {
  i_cardStatus: VitaleCardStatusEnum
  s_serial: string
  i_vitaleVersion: VitaleCardVersionEnum
  s_vitaleVersion: string
  i_cardNature: VitaleCardNatureEnum
  s_cardNature: string
}

export type GetInteropCodesResponse = BaseResponse & Record<InteropCodeId, InteropCode[] | undefined>
